import { useCallback, useEffect, useState } from "react";
import { getOpsSettings, patchOpsSettings } from "@/lib/opsPaperworkClient";
import {
  fetchRoleplayScenarioList,
  postRoleplayLibrary,
  type RoleplayOrgScenarioRow,
  type RoleplayScenarioListItem,
} from "@/lib/training/trainingApiClient";

function emptyScenarioRow(): RoleplayOrgScenarioRow {
  const k = `scenario_${Date.now()}`;
  return {
    id: "",
    scenario_key: k,
    label: "New scenario",
    persona: "You are a prospective customer visiting a portable building dealer. You have realistic questions and mild objections.",
    is_active: false,
    is_sharable: false,
    sort_order: 0,
    kb_context_digest: null,
    trainer_rubric_markdown: null,
    example_questions: [],
    ideal_answer_outline: null,
  };
}

export function useRoleplayTrainingSection(organizationId: string) {
  const [manufacturerOptions, setManufacturerOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [useMfgScenarios, setUseMfgScenarios] = useState(false);
  const [mfgSourceId, setMfgSourceId] = useState("");
  const [freezeKbDigest, setFreezeKbDigest] = useState(false);
  const [mergedPreview, setMergedPreview] = useState<RoleplayScenarioListItem[]>([]);
  const [rows, setRows] = useState<RoleplayOrgScenarioRow[]>([]);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<"suggest" | "qa" | null>(null);
  const [topicPrompt, setTopicPrompt] = useState("");

  const reloadAll = useCallback(async () => {
    setMsg(null);
    const [opsRes, scRes] = await Promise.all([
      getOpsSettings(),
      fetchRoleplayScenarioList(organizationId, { orgEditor: true, includePlatformExample: true }),
    ]);
    if (opsRes.ok && opsRes.data) {
      const envelope = opsRes.data as Record<string, unknown>;
      const settingsRow = (envelope.settings as Record<string, unknown>) || {};
      const options = Array.isArray(envelope.manufacturer_options)
        ? (envelope.manufacturer_options as Array<{ id: string; name: string }>)
        : [];
      setManufacturerOptions(options);
      setUseMfgScenarios(settingsRow.roleplay_use_manufacturer_scenarios === true);
      const mid =
        typeof settingsRow.roleplay_source_manufacturer_org_id === "string" && settingsRow.roleplay_source_manufacturer_org_id
          ? String(settingsRow.roleplay_source_manufacturer_org_id)
          : options[0]?.id ?? "";
      setMfgSourceId(mid);
      setFreezeKbDigest(settingsRow.roleplay_freeze_kb_digest === true);
    }
    if (!scRes.ok) {
      setMsg({ text: scRes.error, ok: false });
      return;
    }
    const d = scRes.data as { scenarios?: RoleplayScenarioListItem[]; orgScenarios?: RoleplayOrgScenarioRow[] };
    setMergedPreview(Array.isArray(d.scenarios) ? d.scenarios : []);
    const org = Array.isArray(d.orgScenarios) ? d.orgScenarios : [];
    setRows(org.map((r) => ({ ...r, example_questions: Array.isArray(r.example_questions) ? r.example_questions : [] })));
  }, [organizationId]);

  useEffect(() => {
    void reloadAll();
  }, [reloadAll]);

  async function saveOrgFlags() {
    setSaving(true);
    setMsg(null);
    const res = await patchOpsSettings({
      roleplay_use_manufacturer_scenarios: useMfgScenarios,
      roleplay_source_manufacturer_org_id: mfgSourceId || null,
      roleplay_freeze_kb_digest: freezeKbDigest,
    });
    setSaving(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setMsg({ text: "Roleplay org settings saved.", ok: true });
    await reloadAll();
  }

  async function saveRow(r: RoleplayOrgScenarioRow) {
    setSaving(true);
    setMsg(null);
    const res = await postRoleplayLibrary(organizationId, {
      op: "upsert",
      row: {
        id: r.id || undefined,
        scenario_key: r.scenario_key.trim(),
        label: r.label.trim(),
        persona: r.persona.trim(),
        is_active: r.is_active,
        is_sharable: r.is_sharable,
        sort_order: r.sort_order,
        kb_context_digest: r.kb_context_digest,
        trainer_rubric_markdown: r.trainer_rubric_markdown,
        example_questions: Array.isArray(r.example_questions) ? r.example_questions : [],
        ideal_answer_outline: r.ideal_answer_outline,
      },
    });
    setSaving(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setMsg({ text: "Scenario saved.", ok: true });
    await reloadAll();
  }

  async function deleteRow(id: string) {
    if (!id) {
      setMsg({ text: "Remove unsaved rows with the Remove button on that card.", ok: false });
      return;
    }
    setSaving(true);
    setMsg(null);
    const res = await postRoleplayLibrary(organizationId, { op: "delete", id });
    setSaving(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setMsg({ text: "Scenario deleted.", ok: true });
    await reloadAll();
  }

  async function runSuggestScenarios() {
    setBusy("suggest");
    setMsg(null);
    const res = await postRoleplayLibrary(organizationId, { op: "suggest_scenarios", topicPrompt: topicPrompt.trim() || "sales training" });
    setBusy(null);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    const drafts = (res.data as { drafts?: unknown[] }).drafts;
    if (!Array.isArray(drafts) || !drafts.length) {
      setMsg({ text: "No drafts returned. Try a different topic.", ok: false });
      return;
    }
    let added = 0;
    for (const d of drafts) {
      const o = d as Record<string, unknown>;
      const key = typeof o.scenario_key === "string" ? o.scenario_key.trim() : "";
      const label = typeof o.label === "string" ? o.label.trim() : "";
      const persona = typeof o.persona === "string" ? o.persona.trim() : "";
      const digest = typeof o.kb_context_digest === "string" ? o.kb_context_digest.trim() : null;
      if (!key || !label || !persona) continue;
      const up = await postRoleplayLibrary(organizationId, {
        op: "upsert",
        row: {
          scenario_key: key,
          label,
          persona,
          is_active: false,
          is_sharable: false,
          sort_order: 0,
          kb_context_digest: digest,
          trainer_rubric_markdown: null,
          example_questions: [],
          ideal_answer_outline: null,
        },
      });
      if (up.ok) added += 1;
    }
    setMsg({ text: added ? `Imported ${added} draft scenario(s) as inactive. Review and activate when ready.` : "No valid drafts imported.", ok: added > 0 });
    await reloadAll();
  }

  async function runSuggestQa(scenarioKey: string) {
    setBusy("qa");
    setMsg(null);
    const res = await postRoleplayLibrary(organizationId, { op: "suggest_qa", scenarioKey });
    setBusy(null);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    const suggestion = (res.data as { suggestion?: Record<string, unknown> }).suggestion;
    if (!suggestion) {
      setMsg({ text: "No QA suggestion returned.", ok: false });
      return;
    }
    setRows((prev) =>
      prev.map((row) => {
        if (row.scenario_key !== scenarioKey) return row;
        return {
          ...row,
          trainer_rubric_markdown:
            typeof suggestion.trainer_rubric_markdown === "string" ? suggestion.trainer_rubric_markdown : row.trainer_rubric_markdown,
          example_questions: Array.isArray(suggestion.example_questions) ? suggestion.example_questions : row.example_questions,
          ideal_answer_outline:
            typeof suggestion.ideal_answer_outline === "string" ? suggestion.ideal_answer_outline : row.ideal_answer_outline,
          kb_context_digest:
            typeof suggestion.kb_context_digest === "string" && suggestion.kb_context_digest.trim()
              ? suggestion.kb_context_digest
              : row.kb_context_digest,
        };
      })
    );
    setMsg({ text: "QA fields filled from KB (not saved until you click Save on the row).", ok: true });
  }

  function addRow() {
    setRows((prev) => [...prev, emptyScenarioRow()]);
  }

  function patchRowAt(index: number, patch: Partial<RoleplayOrgScenarioRow>) {
    setRows((prev) => {
      const next = [...prev];
      if (index < 0 || index >= next.length) return prev;
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function removeRowAt(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function publishRowAt(index: number) {
    const r = rows[index];
    if (!r) return;
    await saveRow({ ...r, is_active: true });
  }

  async function unpublishRowAt(index: number) {
    const r = rows[index];
    if (!r) return;
    await saveRow({ ...r, is_active: false });
  }

  return {
    organizationId,
    manufacturerOptions,
    useMfgScenarios,
    setUseMfgScenarios,
    mfgSourceId,
    setMfgSourceId,
    freezeKbDigest,
    setFreezeKbDigest,
    mergedPreview,
    rows,
    msg,
    saving,
    busy,
    topicPrompt,
    setTopicPrompt,
    reloadAll,
    saveOrgFlags,
    saveRow,
    deleteRow,
    runSuggestScenarios,
    runSuggestQa,
    addRow,
    patchRowAt,
    removeRowAt,
    publishRowAt,
    unpublishRowAt,
  };
}

export type RoleplayTrainingSectionVm = ReturnType<typeof useRoleplayTrainingSection>;
